import React from "react";

/**
 * The fund's NAV path through one trading session.
 *
 * WHY THIS IS ITS OWN COMPONENT AND NOT THE RISK PAGE'S CHART
 * -----------------------------------------------------------
 * The existing surface chart in RiskDashboard.tsx filters to points that have values and
 * then joins the survivors with straight lines. Fed a gappy NAV series it would draw a
 * confident straight line across a two-hour broker outage with nothing to indicate that
 * anything was missing — the reassurance defect this platform keeps producing. Here the
 * line is deliberately split into segments at every gap, so a hole in the data is a hole
 * on the screen.
 *
 * TWO DESIGN RULES THAT LOOK LIKE POLISH AND ARE NOT
 * --------------------------------------------------
 * 1. The x-axis is PINNED to the full session, never fitted to the data. A curve that
 *    started late shows as blank space on the left. An auto-fitted axis would stretch a
 *    partial day to full width and hide the fact entirely.
 * 2. A non-session renders one line of text, never an empty chart. "No data" on an empty
 *    axis reads like a broken system and teaches the eye to ignore the message on the day
 *    it is real.
 */

export type NavIntradaySeries = {
  date: string;
  is_session: boolean;
  session_open_utc?: string | null;
  times: string[];
  /** null wherever no usable NAV exists for that minute — the line MUST break here. */
  values: (number | null)[];
  recon_offsets?: (number | null)[];
  expected_points: number;
  captured_points: number;
  valued_points: number;
  gaps: { from: string; to: string; reason: string | null }[];
  summary: {
    open: number;
    high: number;
    low: number;
    last: number;
    change: number;
    change_pct: number | null;
    /** Change measured against the PRIOR SESSION'S mark of record (dbo.PortfolioNAV),
     *  the institutional basis. The open-based pair above is kept for compatibility —
     *  the first captured mark can be a stale near-close value at 09:30 ET, so a
     *  vs-open change can understate the day. */
    change_vs_prior_close?: number | null;
    change_vs_prior_close_pct?: number | null;
  } | null;
  /** Prior session's daily mark of record, and its date. The chart's % axis is based
   *  here when present; when absent it falls back to the first captured mark AND SAYS
   *  SO — the two bases can differ materially on a gap day. */
  prior_close?: number | null;
  prior_close_date?: string | null;
  /** Ledger events effective in (prior close, this session] — an admission's sweep or
   *  a fee wire lands as an intraday broker-cash step that is CAPITAL MOVEMENT, not a
   *  market move. Noted on the strip; the exact sweep minute is banking, not data. */
  flows?: { date: string; amount: number; kind: string }[] | null;
  basis?: string;
  tier?: string;
};

/** Human word for a ledger row kind, chart-wide. A fee wire is an internal transfer
 *  (IBKR→Axos to pay fees), NOT a capital flow — the label must not lump them. */
export const flowKindLabel = (kind: string): string => {
  const k = (kind || "").toUpperCase();
  return k === "SUBSCRIPTION" ? "subscription" : k === "REDEMPTION" ? "redemption" : k === "FEE_PAYMENT" ? "fee wire" : k.toLowerCase();
};

/** Δ% with the window's capital movement removed from the numerator — the SAME
 *  subtraction the Est.-return tile makes (subscriptions/redemptions out; a fee
 *  wire's broker dent removed), so the chart and the tile finally reconcile on
 *  screen instead of leaving the reader to subtract flows in their head
 *  (CIO request 2026-09-02: the 2D chart read −0.55% while the tile read −0.97%,
 *  and the difference was exactly the $100K September admission). Null without a
 *  sound base, a numeric change, or any flow in the window — the raw Δ already IS
 *  the ex-flows figure then, and printing a duplicate would imply a computation
 *  that never ran. Null (never a partial sum) if any flow amount is non-numeric.
 *  Exported for tests. */
export function exFlowsPct(
  changeUsd: number | null | undefined,
  base: number | null | undefined,
  flows: { amount: number }[] | null | undefined,
): number | null {
  if (typeof changeUsd !== "number" || !Number.isFinite(changeUsd)) return null;
  if (typeof base !== "number" || !Number.isFinite(base) || base <= 0) return null;
  if (!flows?.length) return null;
  let sum = 0;
  for (const f of flows) {
    if (typeof f.amount !== "number" || !Number.isFinite(f.amount)) return null;
    sum += f.amount;
  }
  return ((changeUsd - sum) / base) * 100;
}

/** One tooltip for the ex-flows chip on both charts — the reconciliation story must
 *  not fork between the intraday and range views. */
const EX_FLOWS_HINT =
  "Δ with the window's capital movement (subscriptions, redemptions, fee wires) removed from the " +
  "numerator — the same subtraction the Est.-return tile makes. On a window matching the tile's " +
  "period (MTD from the official strike), this reconciles to the tile's 'before fees' figure; what " +
  "remains is the modelled management-fee accrual and the anchor basis (the chart anchors at the " +
  "IBKR close mark, the tile at the official strike NAV — a few bps). Estimated, unofficial. " +
  "NOTE: the multi-session ranges (2D and longer) use a CHAINED per-unit index instead of this " +
  "single subtraction — the two agree closely over a short window and diverge over long ones, " +
  "where only the chained form is a true per-unit return.";

/**
 * Split a value series into contiguous runs of real numbers.
 *
 * THIS IS THE FUNCTION THAT KEEPS THE CHART HONEST. Each run is drawn as its own path, so
 * a gap renders as an absence of line. The alternative — filtering out the nulls and
 * joining what is left — draws a straight, confident line across an outage, which is
 * indistinguishable from a calm market. Exported so that property is tested directly
 * rather than only observed.
 *
 * null, undefined, NaN and Infinity all count as gaps.
 */
export function splitSegments(
  values: (number | null | undefined)[],
): { i: number; v: number }[][] {
  const segments: { i: number; v: number }[][] = [];
  let current: { i: number; v: number }[] = [];
  values.forEach((v, i) => {
    if (typeof v === "number" && Number.isFinite(v)) {
      current.push({ i, v });
    } else if (current.length) {
      segments.push(current);
      current = [];
    }
  });
  if (current.length) segments.push(current);
  return segments;
}

/**
 * Place each captured mark into its true minute-of-session slot.
 *
 * WHY (fixed 2026-09-01, first live session): the first render plotted points by ARRAY
 * position, so four marks captured at 07:30-07:33 occupied slots 0-3 — but so would four
 * marks captured at 11:00-11:03. A late start packed left instead of showing blank space,
 * and a minute where the timer never fired (no row at all) was simply absent from the
 * array, letting the line draw straight across it. Expanding into a full-length
 * slot array makes all three cases fall out of splitSegments with no extra logic:
 * late start = leading nulls, timer-down = interior nulls, recorded gap = its own null.
 */
export function buildSlotSeries(
  times: string[],
  values: (number | null)[],
  sessionOpenUtc: string | null | undefined,
  expectedPoints: number,
): (number | null)[] {
  const n = Math.max(expectedPoints, 1);
  const slots: (number | null)[] = new Array(n).fill(null);
  const open = sessionOpenUtc ? Date.parse(sessionOpenUtc) : times.length ? Date.parse(times[0]) : NaN;
  if (!Number.isFinite(open)) return slots;
  times.forEach((t, i) => {
    const ts = Date.parse(t);
    if (!Number.isFinite(ts)) return;
    const slot = Math.round((ts - open) / 60_000);
    if (slot >= 0 && slot < n) slots[slot] = values[i] ?? null;
  });
  return slots;
}

/**
 * Geometry. The svg renders `w-full h-auto` so its HEIGHT FOLLOWS ITS WIDTH via the
 * viewBox aspect — the previous fixed h-[210px] letterboxed a 560-wide viewBox inside
 * a wider card, and the blank margins read as a broken layout (CIO, 2026-09-01).
 */
const W = 760;
const H = 236;
const PAD = { top: 10, right: 14, bottom: 26, left: 52 };

const fmtUSD = (v: number | null | undefined, digits = 0) =>
  typeof v === "number" && Number.isFinite(v)
    ? v.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: digits })
    : "—";

const fmtClock = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};

/** Signed percent for axis ticks and deltas. Direction is carried by SIGN, never by
 *  color — the system-wide rule (cockpit/delta.tsx); a gain does not turn this green. */
const fmtPctSigned = (p: number | null | undefined, digits = 2) =>
  typeof p === "number" && Number.isFinite(p)
    ? `${p > 0 ? "+" : p < 0 ? "−" : ""}${Math.abs(p).toFixed(digits)}%`
    : "—";

/** Clean tick positions: multiples of a round step, chosen so the span carries ≤ ~6.
 *  Exported so the step choice is testable rather than eyeballed. */
export function pctTicks(lo: number, hi: number): number[] {
  const span = Math.max(hi - lo, 1e-6);
  const step = [0.05, 0.1, 0.2, 0.25, 0.5, 1, 2, 5].find((c) => span / c <= 6) ?? 10;
  const out: number[] = [];
  for (let t = Math.ceil(lo / step) * step; t <= hi + 1e-9; t += step) {
    out.push(Math.abs(t) < 1e-9 ? 0 : Number(t.toFixed(4)));
  }
  return out;
}

export const NavIntradayChart: React.FC<{ series: NavIntradaySeries | null; loading?: boolean }> = ({
  series,
  loading,
}) => {
  const [hover, setHover] = React.useState<{ slot: number; usd: number; pct: number } | null>(null);
  const svgRef = React.useRef<SVGSVGElement | null>(null);

  if (loading) {
    return <div className="text-[11px] opacity-70">Loading session…</div>;
  }
  if (!series) {
    return <div className="text-[11px] opacity-70">No intraday data available.</div>;
  }
  if (!series.is_session) {
    return (
      <div className="text-[11px] opacity-80">
        {new Date(series.date + "T12:00:00").toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })}{" "}
        was not a trading session.
      </div>
    );
  }

  const vals = buildSlotSeries(
    series.times ?? [],
    series.values ?? [],
    series.session_open_utc,
    series.expected_points || (series.values ?? []).length,
  );
  const usable = vals.filter((v): v is number => typeof v === "number" && Number.isFinite(v));

  if (!usable.length) {
    return (
      <div className="text-[11px]" style={{ color: "rgb(180,118,8)" }}>
        No NAV marks were captured for this session ({series.captured_points} of{" "}
        {series.expected_points} minutes recorded).
      </div>
    );
  }

  // --- The % basis -----------------------------------------------------------
  // Prior session's mark of record when the backend supplies it (the institutional
  // convention), else the first captured mark — and the strip SAYS which, because the
  // first 09:30 mark can be a stale near-close value and the two bases then disagree.
  const priorClose =
    typeof series.prior_close === "number" && Number.isFinite(series.prior_close) && series.prior_close > 0
      ? series.prior_close
      : null;
  const base = priorClose ?? usable[0];
  const baseIsPrior = priorClose !== null;

  const pvals: (number | null)[] = vals.map((v) =>
    typeof v === "number" && Number.isFinite(v) ? (v / base - 1) * 100 : null,
  );
  const pUsable = pvals.filter((v): v is number => typeof v === "number");

  // Domain always includes the 0% baseline — where the day sits vs prior close is the
  // question this chart answers, so the reference line must be on screen.
  const rawLo = Math.min(...pUsable, 0);
  const rawHi = Math.max(...pUsable, 0);
  const span = rawHi - rawLo || 0.1;
  const lo = rawLo - span * 0.1;
  const hi = rawHi + span * 0.1;

  const denom = Math.max(vals.length - 1, 1);
  const xAt = (i: number) => PAD.left + (i / denom) * (W - PAD.left - PAD.right);
  const yAt = (p: number) => PAD.top + (1 - (p - lo) / (hi - lo)) * (H - PAD.top - PAD.bottom);

  const segments = splitSegments(pvals);
  const pathFor = (seg: { i: number; v: number }[]) =>
    seg.map((pt, k) => `${k === 0 ? "M" : "L"} ${xAt(pt.i).toFixed(2)} ${yAt(pt.v).toFixed(2)}`).join(" ");

  const ticks = pctTicks(lo, hi);
  // 2 decimals whenever ANY tick is not a clean tenth — otherwise a 0.25 step
  // renders as "+0.3%" beside "+0.5%" and the axis reads as unevenly spaced.
  const tickDigits = ticks.some((t) => Math.abs(t * 10 - Math.round(t * 10)) > 1e-9) ? 2 : 1;

  // Hour marks: the slots where the wall clock sits on a whole hour.
  const openMs = series.session_open_utc ? Date.parse(series.session_open_utc) : NaN;
  const hourSlots: number[] = [];
  if (Number.isFinite(openMs)) {
    const openMin = new Date(openMs).getUTCMinutes();
    for (let sl = (60 - (openMin % 60)) % 60; sl < vals.length; sl += 60) hourSlots.push(sl);
  }

  const missing = Math.max(series.expected_points - series.valued_points, 0);
  const incomplete = missing > 0;
  const lastSeg = segments.length ? segments[segments.length - 1] : null;
  const lastPt = lastSeg && lastSeg.length ? lastSeg[lastSeg.length - 1] : null;

  const changePct = baseIsPrior
    ? (usable[usable.length - 1] / base - 1) * 100
    : series.summary?.change_pct ?? null;
  const changeUsd = baseIsPrior ? usable[usable.length - 1] - base : series.summary?.change ?? null;
  // Only meaningful against the prior-close base: a first-mark base already sits
  // after any pre-open capital movement, so subtracting the flow would double-remove.
  const exPct = baseIsPrior ? exFlowsPct(changeUsd, base, series.flows) : null;

  // Hover reads the nearest CAPTURED minute; a gap under the pointer shows nothing
  // rather than an interpolated value the book never printed.
  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const el = svgRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (!rect.width) return;
    const x = ((e.clientX - rect.left) / rect.width) * W;
    const slot = Math.round(((x - PAD.left) / (W - PAD.left - PAD.right)) * denom);
    let found: number | null = null;
    for (let d = 0; d <= 5 && found === null; d++) {
      for (const c of d === 0 ? [slot] : [slot - d, slot + d]) {
        const v = vals[c];
        if (typeof v === "number" && Number.isFinite(v)) {
          found = c;
          break;
        }
      }
    }
    if (found === null) {
      setHover(null);
      return;
    }
    const usd = vals[found] as number;
    setHover({ slot: found, usd, pct: (usd / base - 1) * 100 });
  };

  const hoverLeftPct = hover ? (xAt(hover.slot) / W) * 100 : 0;
  const hoverTopPct = hover ? (yAt(hover.pct) / H) * 100 : 0;

  return (
    <div className="w-full">
      <div className="relative w-full">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          className="block w-full h-auto"
          role="img"
          aria-label={`Intraday NAV for ${series.date}, percent vs ${baseIsPrior ? "prior close" : "first mark"}`}
          onPointerMove={onMove}
          onPointerLeave={() => setHover(null)}
        >
          {/* Recessive horizontal grid: hairline, SOLID (dashes read as annotation).
              The 0% line — prior close — sits a step darker as the reference. */}
          {ticks.map((t) => (
            <g key={`t${t}`}>
              <line
                x1={PAD.left}
                y1={yAt(t)}
                x2={W - PAD.right}
                y2={yAt(t)}
                stroke={t === 0 ? "var(--chart-axis-text)" : "var(--chart-grid-line)"}
                strokeOpacity={t === 0 ? 0.5 : 1}
                strokeWidth={1}
              />
              <text
                x={PAD.left - 8}
                y={yAt(t) + 3.5}
                textAnchor="end"
                className="fill-[var(--chart-axis-text)] text-[10px]"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {fmtPctSigned(t, tickDigits)}
              </text>
            </g>
          ))}

          {/* Session ruler: baseline + hour ticks. The x-domain stays PINNED to the
              full session, so a partial day reads as exactly that. */}
          <line
            x1={PAD.left}
            y1={H - PAD.bottom + 4}
            x2={W - PAD.right}
            y2={H - PAD.bottom + 4}
            stroke="var(--chart-grid-line)"
            strokeWidth={1}
          />
          {hourSlots.map((sl) => (
            <g key={`h${sl}`}>
              <line
                x1={xAt(sl)}
                y1={H - PAD.bottom + 4}
                x2={xAt(sl)}
                y2={H - PAD.bottom + 8}
                stroke="var(--chart-axis-text)"
                strokeOpacity={0.5}
                strokeWidth={1}
              />
              <text
                x={xAt(sl)}
                y={H - 8}
                textAnchor="middle"
                className="fill-[var(--chart-axis-text)] text-[10px]"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {fmtClock(new Date(openMs + sl * 60_000).toISOString())}
              </text>
            </g>
          ))}

          {/* One path per contiguous run. A gap is an absence of line, not a straight one. */}
          {segments.map((seg, idx) => (
            <path
              key={`seg${idx}`}
              d={pathFor(seg)}
              fill="none"
              stroke="rgb(var(--c-live))"
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}

          {/* Dots where a line cannot speak: a young session and single-point segments. */}
          {segments.map((seg, idx) =>
            pUsable.length <= 30 || seg.length === 1
              ? seg.map((pt) => (
                  <circle key={`dot${idx}-${pt.i}`} cx={xAt(pt.i)} cy={yAt(pt.v)} r={1.8} fill="rgb(var(--c-live))" />
                ))
              : null,
          )}

          {/* Hover crosshair + ringed marker at the true captured minute. */}
          {hover ? (
            <g pointerEvents="none">
              <line
                x1={xAt(hover.slot)}
                y1={PAD.top}
                x2={xAt(hover.slot)}
                y2={H - PAD.bottom + 4}
                stroke="var(--chart-axis-text)"
                strokeOpacity={0.35}
                strokeWidth={1}
              />
              <circle
                cx={xAt(hover.slot)}
                cy={yAt(hover.pct)}
                r={4}
                fill="rgb(var(--c-live))"
                stroke="var(--card-bg)"
                strokeWidth={2}
              />
            </g>
          ) : null}

          {/* The newest mark, always emphasised, ringed in the surface color. */}
          {lastPt ? (
            <circle
              cx={xAt(lastPt.i)}
              cy={yAt(lastPt.v)}
              r={4}
              fill="rgb(var(--c-live))"
              stroke="var(--card-bg)"
              strokeWidth={2}
            />
          ) : null}
        </svg>

        {hover ? (
          <div
            className="pointer-events-none absolute z-10 rounded border px-2 py-1 text-[11px] leading-4 shadow-sm"
            style={{
              left: `${hoverLeftPct}%`,
              top: `${hoverTopPct}%`,
              transform: `translate(${hoverLeftPct > 78 ? "-105%" : "8px"}, -110%)`,
              background: "var(--card-bg)",
              borderColor: "var(--card-border)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            <div style={{ opacity: 0.7 }}>
              {Number.isFinite(openMs) ? fmtClock(new Date(openMs + hover.slot * 60_000).toISOString()) : "—"}
            </div>
            <div>
              {fmtUSD(hover.usd)} · {fmtPctSigned(hover.pct)}
            </div>
          </div>
        ) : null}
      </div>

      {/* Stat strip. Δ leads and names its basis; capture health keeps its amber voice. */}
      <div
        className="mt-2 flex flex-wrap items-baseline gap-x-5 gap-y-1 text-[11px]"
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        <span>
          <span style={{ opacity: 0.65 }}>Last </span>
          <strong>{fmtUSD(series.summary?.last)}</strong>
        </span>
        <span>
          <span style={{ opacity: 0.65 }}>{baseIsPrior ? "Δ vs prior close " : "Δ vs first mark "}</span>
          <strong>{fmtPctSigned(changePct)}</strong>
          <span style={{ opacity: 0.8 }}>
            {" "}
            ({typeof changeUsd === "number" ? `${changeUsd >= 0 ? "+" : "−"}${fmtUSD(Math.abs(changeUsd))}` : "—"})
          </span>
        </span>
        {baseIsPrior ? (
          <span style={{ opacity: 0.65 }}>
            prior close {fmtUSD(base)}
            {series.prior_close_date ? ` (${series.prior_close_date.slice(5)})` : ""}
          </span>
        ) : (
          <span style={{ color: "rgb(180,118,8)" }}>prior close unavailable — % measured from the first mark</span>
        )}
        <span style={{ opacity: 0.65 }}>
          O {fmtUSD(series.summary?.open)} · H {fmtUSD(series.summary?.high)} · L {fmtUSD(series.summary?.low)}
        </span>
        <span style={{ color: incomplete ? "rgb(180,118,8)" : undefined, opacity: incomplete ? 1 : 0.65 }}>
          {series.valued_points} of {series.expected_points} marks
        </span>
        {exPct !== null ? (
          <span title={EX_FLOWS_HINT}>
            <span style={{ opacity: 0.65 }}>ex-flows </span>
            <strong>≈ {fmtPctSigned(exPct)}</strong>
          </span>
        ) : null}
        {series.flows?.length ? (
          <span
            style={{ opacity: 0.8 }}
            title="Capital movement in this session's window. The sweep/wire lands as an intraday broker-cash step at some minute of the day — that step is capital moving, not the market."
          >
            {series.flows
              .map((f) => `${f.amount >= 0 ? "+" : "−"}$${(Math.abs(f.amount) / 1000).toFixed(0)}K ${flowKindLabel(f.kind)}`)
              .join(" · ")}{" "}
            in window — cash steps are not performance
          </span>
        ) : null}
      </div>

      {series.gaps?.length ? (
        <div className="mt-0.5 text-[11px]" style={{ color: "rgb(180,118,8)" }}>
          No marks{" "}
          {series.gaps
            .slice(0, 3)
            .map((g) => `${fmtClock(g.from)}–${fmtClock(g.to)}`)
            .join(", ")}
          {series.gaps.length > 3 ? ` and ${series.gaps.length - 3} more` : ""}
          {series.gaps[0]?.reason ? ` (${series.gaps[0].reason.toLowerCase().replace(/_/g, " ")})` : ""}.
        </div>
      ) : null}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Range mode (2D / 5D / MTD / QTD / YTD / ITD) — added 2026-09-01, CIO request.
// ---------------------------------------------------------------------------

export type NavRangeSeries = {
  mode: "minute" | "daily";
  range: string;
  date: string;
  /** % base: the daily close strictly before the range start ("prior_close"), or the
   *  first mark in range ("first_mark") when no earlier history exists (ITD). */
  base_value: number | null;
  base_date: string | null;
  base_kind: string;
  /** minute mode: per-session minute series (capture began 2026-09-01 — earlier
   *  sessions arrive with no values and render as labeled holes, never borrowed
   *  daily marks at a different granularity). */
  sessions: {
    date: string;
    session_open_utc: string;
    times: string[];
    values: (number | null)[];
    /** Per-unit index, 1:1 with `values` (see `index_available`). */
    index?: (number | null)[];
    expected_points: number;
    valued_points: number;
  }[];
  /** daily mode: one slot per SESSION; nav null = no mark of record = hole. */
  days: { date: string; nav: number | null; index?: number | null }[];
  /** Per-unit index (1.0 at the base) with capital flows divided out at their
   *  effective date — the administrator's NAV-Per-Share construction. This is the
   *  PERFORMANCE line; `nav`/`values` remain the LEVEL. Absent on older backends,
   *  in which case the chart falls back to the level line it always drew. */
  index_available?: boolean;
  /** True only when a flow was actually divided out — the label must not claim a
   *  flow adjustment on a window that contained no flows. */
  flow_adjusted?: boolean;
  /** True when today's slot carries the live intraday mark rather than a daily
   *  mark of record (which is only promoted at 14:05 MT). */
  today_live?: boolean;
  /** Official month-end NAV-Per-Share strikes, rebased to the same index — the
   *  administrator's audited per-unit truth, drawn as anchor points. */
  navps_anchors?: { date: string; navps: number; index: number }[];
  /** True when every CLOSED month in the window is PINNED to the administrator's
   *  audited NAVPS, so our own history's basis changes cannot accumulate into the
   *  line. The segment after `index_unanchored_from` has no official answer yet and
   *  is broker-derived — the chart must distinguish the two. */
  index_anchored?: boolean;
  index_anchor_dates?: string[];
  index_unanchored_from?: string | null;
  /** False when the window contains no closed month to pin — MTD's base IS the last
   *  strike, so "unanchored" is the complete and correct answer there. Without this
   *  the drift warning would fire on the most accurate range on the page. */
  index_anchor_possible?: boolean;
  /** Where the audited section STARTS. On a window whose base has no comparable official
   *  strike (ITD, based on the fund's first broker mark), everything before this date is
   *  our own unaudited marks and must be labelled as such. */
  index_anchored_from?: string | null;
  /** Sessions withheld from the per-unit line because a material admission was still
   *  in transit from Axos — the broker book held only part of the fund, so no honest
   *  per-unit return exists for them. The line breaks; this says why. */
  settling_dates?: string[];
  /** Today's newest minute mark (today's daily mark is only written at 13:45 MT). */
  live_last: { as_of_utc: string; nav: number } | null;
  /** Capital flows inside the window — MARKED on the chart, because a long-range NAV
   *  line legitimately contains them and the axis must not read as performance. */
  flows: { date: string; amount: number; kind: string }[];
  pre_record_history?: boolean;
};

/** Daily-mode slots: one per session, today's live mark riding as the final value
 *  when the daily mark of record has not been written yet. Exported for tests. */
export function buildDailySlots(
  days: { date: string; nav: number | null }[],
  liveLast: { as_of_utc: string; nav: number } | null | undefined,
): (number | null)[] {
  const slots = days.map((d) => (typeof d.nav === "number" && Number.isFinite(d.nav) ? d.nav : null));
  if (slots.length && slots[slots.length - 1] === null && liveLast && Number.isFinite(liveLast.nav)) {
    slots[slots.length - 1] = liveLast.nav;
  }
  return slots;
}

const RANGE_LABEL: Record<string, string> = {
  "2d": "2 sessions",
  "5d": "5 sessions",
  mtd: "month to date",
  qtd: "quarter to date",
  ytd: "year to date",
  itd: "inception to date",
};

export const NavRangeChart: React.FC<{ series: NavRangeSeries | null; loading?: boolean }> = ({
  series,
  loading,
}) => {
  const [hover, setHover] = React.useState<{ slot: number; usd: number; pct: number; label: string } | null>(null);
  // Default to the PERFORMANCE line (flows divided out). The level line stays one
  // click away because fund size is a real thing to want — it just is not return
  // (CIO 2026-09-03: the long-range chart was reading a subscription as growth).
  const [plotMode, setPlotMode] = React.useState<"return" | "level">("return");
  const svgRef = React.useRef<SVGSVGElement | null>(null);

  // The loading line is for when there is NOTHING to show yet — never for a refresh of a
  // series already on screen. Both mounts refetch every minute (Signals on a clock,
  // Portfolio on the SignalR nav push), and swapping the whole chart for this one line
  // during each fetch blinked it out and back once a minute (CIO, 2026-09-11). A range
  // SWITCH still shows it: both callers pass series=null until the new range's payload
  // lands, so the previous range can never render under the new label.
  if (loading && !series) return <div className="text-[11px] opacity-70">Loading range…</div>;
  if (!series) return <div className="text-[11px] opacity-70">No range data available.</div>;

  const minuteMode = series.mode === "minute";

  // --- Assemble the slot series -------------------------------------------
  let vals: (number | null)[] = [];
  const sessionSpans: { date: string; from: number; to: number; open: string; empty: boolean }[] = [];
  let slotLabel: (slot: number) => string = () => "—";

  let ivals: (number | null)[] = [];

  if (minuteMode) {
    for (const sess of series.sessions ?? []) {
      const from = vals.length;
      const seg = buildSlotSeries(sess.times ?? [], sess.values ?? [], sess.session_open_utc, sess.expected_points || 390);
      // Same placement call on the parallel index array, so a minute's index can
      // never land on a different minute than its NAV.
      ivals = ivals.concat(
        buildSlotSeries(sess.times ?? [], sess.index ?? [], sess.session_open_utc, sess.expected_points || 390),
      );
      vals = vals.concat(seg);
      sessionSpans.push({
        date: sess.date,
        from,
        to: vals.length,
        open: sess.session_open_utc,
        empty: (sess.valued_points ?? 0) === 0,
      });
    }
    slotLabel = (slot) => {
      const span = sessionSpans.find((sp) => slot >= sp.from && slot < sp.to);
      if (!span) return "—";
      const t = Date.parse(span.open) + (slot - span.from) * 60_000;
      return `${span.date.slice(5)} ${fmtClock(new Date(t).toISOString())}`;
    };
  } else {
    vals = buildDailySlots(series.days ?? [], series.live_last);
    ivals = (series.days ?? []).map((d) => (typeof d.index === "number" && Number.isFinite(d.index) ? d.index : null));
    slotLabel = (slot) => series.days?.[slot]?.date ?? "—";
  }

  const usable = vals.filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  if (!usable.length) {
    return (
      <div className="text-[11px]" style={{ color: "rgb(180,118,8)" }}>
        No NAV marks exist for this range yet
        {minuteMode ? " — minute capture began 09-01-2026 and fills in from there" : ""}.
      </div>
    );
  }

  const base =
    typeof series.base_value === "number" && Number.isFinite(series.base_value) && series.base_value > 0
      ? series.base_value
      : usable[0];
  const baseIsPrior = series.base_kind === "prior_close" && series.base_value != null;

  // The return line needs BOTH a backend that supplies the index and at least one
  // usable point; otherwise fall back to the level line rather than render an empty
  // chart under a "return" label.
  const indexUsable = !!series.index_available && ivals.some((v) => typeof v === "number" && Number.isFinite(v));
  const mode: "return" | "level" = indexUsable && plotMode === "return" ? "return" : "level";

  const pvals: (number | null)[] =
    mode === "return"
      ? ivals.map((ix) => (typeof ix === "number" && Number.isFinite(ix) ? (ix - 1) * 100 : null))
      : vals.map((v) => (typeof v === "number" && Number.isFinite(v) ? (v / base - 1) * 100 : null));
  const pUsable = pvals.filter((v): v is number => typeof v === "number");

  // Level and per-unit deltas, both always computed so the footer can show the one
  // the reader is NOT looking at (the two disagree exactly by the window's flows).
  const lastIdx = [...ivals].reverse().find((v): v is number => typeof v === "number" && Number.isFinite(v));
  const unitPct = typeof lastIdx === "number" ? (lastIdx - 1) * 100 : null;

  // Official anchor percentages join the domain: an anchor that disagrees with our
  // line by more than the plotted span would otherwise render outside the viewBox and
  // be silently clipped — the disagreement disappearing precisely when it is largest.
  const anchorPcts =
    !minuteMode && series.index_available
      ? (series.navps_anchors ?? []).map((a) => (a.index - 1) * 100).filter((p) => Number.isFinite(p))
      : [];
  const domainPts = pUsable.concat(anchorPcts);
  const rawLo = Math.min(...domainPts, 0);
  const rawHi = Math.max(...domainPts, 0);
  const span = rawHi - rawLo || 0.1;
  const lo = rawLo - span * 0.1;
  const hi = rawHi + span * 0.1;

  const denom = Math.max(vals.length - 1, 1);
  const xAt = (i: number) => PAD.left + (i / denom) * (W - PAD.left - PAD.right);
  const yAt = (p: number) => PAD.top + (1 - (p - lo) / (hi - lo)) * (H - PAD.top - PAD.bottom);

  const segments = splitSegments(pvals);
  const pathFor = (seg: { i: number; v: number }[]) =>
    seg.map((pt, k) => `${k === 0 ? "M" : "L"} ${xAt(pt.i).toFixed(2)} ${yAt(pt.v).toFixed(2)}`).join(" ");

  const ticks = pctTicks(lo, hi);
  // 2 decimals whenever ANY tick is not a clean tenth — otherwise a 0.25 step
  // renders as "+0.3%" beside "+0.5%" and the axis reads as unevenly spaced.
  const tickDigits = ticks.some((t) => Math.abs(t * 10 - Math.round(t * 10)) > 1e-9) ? 2 : 1;

  // X labels: session dividers in minute mode; ≤6 evenly-spaced dates in daily mode.
  const xLabels: { slot: number; text: string }[] = [];
  if (minuteMode) {
    for (const sp of sessionSpans) xLabels.push({ slot: Math.floor((sp.from + sp.to) / 2), text: sp.date.slice(5) });
  } else {
    const n = vals.length;
    const step = Math.max(1, Math.ceil(n / 6));
    for (let i = 0; i < n; i += step) xLabels.push({ slot: i, text: (series.days?.[i]?.date ?? "").slice(5) });
    if (n > 1 && (n - 1) % step !== 0) xLabels.push({ slot: n - 1, text: (series.days?.[n - 1]?.date ?? "").slice(5) });
  }

  // Flow markers at their slot.
  const flowMarks = (series.flows ?? [])
    .map((f) => {
      const slot = minuteMode
        ? sessionSpans.find((sp) => sp.date === f.date)?.from ?? -1
        : (series.days ?? []).findIndex((d) => d.date >= f.date);
      return slot >= 0 ? { ...f, slot } : null;
    })
    .filter((f): f is { date: string; amount: number; kind: string; slot: number } => f !== null);

  // Official NAVPS strikes, rebased to the same index. Return mode only: against a
  // LEVEL line (which contains flows) a per-share point is not comparable and would
  // read as the administrator disagreeing with us.
  const anchorMarks =
    mode === "return" && !minuteMode
      ? (series.navps_anchors ?? [])
          .map((a) => {
            // Snap BACKWARD to the last session at-or-before the strike — that close is
            // what the strike is struck against. Snapping forward (the first session
            // at-or-after) lands a month-end that fell on a weekend — 4 of 12 in 2026 —
            // on the NEXT session, displacing the official dot from the line by a whole
            // session's move on a marker captioned as the truth the line should track.
            const days = series.days ?? [];
            let slot = -1;
            for (let i = 0; i < days.length; i++) {
              if (days[i].date <= a.date) slot = i;
              else break;
            }
            return slot >= 0 ? { ...a, slot, pct: (a.index - 1) * 100 } : null;
          })
          .filter((a): a is { date: string; navps: number; index: number; slot: number; pct: number } => a !== null)
      : [];

  const lastSeg = segments.length ? segments[segments.length - 1] : null;
  const lastPt = lastSeg && lastSeg.length ? lastSeg[lastSeg.length - 1] : null;
  const lastUsd = usable[usable.length - 1];
  const changePct = (lastUsd / base - 1) * 100;
  const changeUsd = lastUsd - base;
  // Prior-close base only — against a first-mark base (ITD before capture began)
  // a same-day flow may already sit inside the base and would be double-removed.
  const exPct = baseIsPrior ? exFlowsPct(changeUsd, base, series.flows) : null;

  const emptySessions = sessionSpans.filter((sp) => sp.empty).map((sp) => sp.date.slice(5));
  const missingDays = minuteMode ? 0 : (series.days ?? []).filter((d) => d.nav === null).length - (series.live_last && (series.days ?? []).length && (series.days ?? [])[(series.days ?? []).length - 1]?.nav === null ? 1 : 0);

  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const el = svgRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (!rect.width) return;
    const x = ((e.clientX - rect.left) / rect.width) * W;
    const slot = Math.round(((x - PAD.left) / (W - PAD.left - PAD.right)) * denom);
    const reach = minuteMode ? 8 : 2;
    let found: number | null = null;
    for (let d = 0; d <= reach && found === null; d++) {
      for (const c of d === 0 ? [slot] : [slot - d, slot + d]) {
        const v = vals[c];
        if (typeof v === "number" && Number.isFinite(v)) {
          found = c;
          break;
        }
      }
    }
    if (found === null) {
      setHover(null);
      return;
    }
    const usd = vals[found] as number;
    const p = pvals[found];
    setHover({
      slot: found,
      usd,
      pct: typeof p === "number" ? p : (usd / base - 1) * 100,
      label: slotLabel(found),
    });
  };

  const hoverLeftPct = hover ? (xAt(hover.slot) / W) * 100 : 0;
  const hoverTopPct = hover ? (yAt(hover.pct) / H) * 100 : 0;

  return (
    <div className="w-full">
      <div className="relative w-full">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          className="block w-full h-auto"
          role="img"
          aria-label={`NAV ${RANGE_LABEL[series.range] ?? series.range}, ${
            mode === "return"
              ? "per-unit percent return with capital flows divided out"
              : "NAV level percent including capital flows"
          }, vs ${baseIsPrior ? "prior close" : "first mark"}`}
          onPointerMove={onMove}
          onPointerLeave={() => setHover(null)}
        >
          {ticks.map((t) => (
            <g key={`t${t}`}>
              <line
                x1={PAD.left}
                y1={yAt(t)}
                x2={W - PAD.right}
                y2={yAt(t)}
                stroke={t === 0 ? "var(--chart-axis-text)" : "var(--chart-grid-line)"}
                strokeOpacity={t === 0 ? 0.5 : 1}
                strokeWidth={1}
              />
              <text
                x={PAD.left - 8}
                y={yAt(t) + 3.5}
                textAnchor="end"
                className="fill-[var(--chart-axis-text)] text-[10px]"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {fmtPctSigned(t, tickDigits)}
              </text>
            </g>
          ))}

          <line
            x1={PAD.left}
            y1={H - PAD.bottom + 4}
            x2={W - PAD.right}
            y2={H - PAD.bottom + 4}
            stroke="var(--chart-grid-line)"
            strokeWidth={1}
          />

          {/* Session dividers (minute mode): a faint boundary where one day ends. */}
          {minuteMode
            ? sessionSpans.slice(1).map((sp) => (
                <line
                  key={`div${sp.date}`}
                  x1={xAt(sp.from)}
                  y1={PAD.top}
                  x2={xAt(sp.from)}
                  y2={H - PAD.bottom + 4}
                  stroke="var(--chart-grid-line)"
                  strokeWidth={1}
                />
              ))
            : null}

          {xLabels.map((l) => (
            <text
              key={`x${l.slot}`}
              x={xAt(l.slot)}
              y={H - 8}
              textAnchor="middle"
              className="fill-[var(--chart-axis-text)] text-[10px]"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {l.text}
            </text>
          ))}

          {/* Capital-flow markers: a NAV step here is capital, not performance. */}
          {flowMarks.map((f) => (
            <path
              key={`fl${f.date}`}
              d={`M ${xAt(f.slot) - 4} ${H - PAD.bottom + 2} L ${xAt(f.slot) + 4} ${H - PAD.bottom + 2} L ${xAt(f.slot)} ${H - PAD.bottom - 5} Z`}
              fill="var(--chart-axis-text)"
              fillOpacity={0.75}
            >
              <title>
                {`${f.amount >= 0 ? "+" : "−"}$${Math.abs(f.amount).toLocaleString()} ${flowKindLabel(f.kind)} effective ${f.date}`}
              </title>
            </path>
          ))}

          {segments.map((seg, idx) => (
            <path
              key={`seg${idx}`}
              d={pathFor(seg)}
              fill="none"
              stroke="rgb(var(--c-live))"
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}

          {/* Official NAV-Per-Share strikes — the administrator's audited per-unit
              truth on the same rebased scale. Hollow so they read as reference
              points against our estimated line, never as part of it. */}
          {anchorMarks.map((a) => (
            <circle
              key={`ps${a.date}`}
              cx={xAt(a.slot)}
              cy={yAt(a.pct)}
              r={3.5}
              fill="var(--card-bg)"
              stroke="var(--chart-axis-text)"
              strokeWidth={1.5}
            >
              <title>
                {`Official NAV per share ${a.date}: $${a.navps.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })} (${a.pct >= 0 ? "+" : "−"}${Math.abs(a.pct).toFixed(2)}% per unit since the base) — NAV Fund Services, official.`}
              </title>
            </circle>
          ))}

          {segments.map((seg, idx) =>
            pUsable.length <= 30 || seg.length === 1
              ? seg.map((pt) => (
                  <circle key={`dot${idx}-${pt.i}`} cx={xAt(pt.i)} cy={yAt(pt.v)} r={1.8} fill="rgb(var(--c-live))" />
                ))
              : null,
          )}

          {hover ? (
            <g pointerEvents="none">
              <line
                x1={xAt(hover.slot)}
                y1={PAD.top}
                x2={xAt(hover.slot)}
                y2={H - PAD.bottom + 4}
                stroke="var(--chart-axis-text)"
                strokeOpacity={0.35}
                strokeWidth={1}
              />
              <circle cx={xAt(hover.slot)} cy={yAt(hover.pct)} r={4} fill="rgb(var(--c-live))" stroke="var(--card-bg)" strokeWidth={2} />
            </g>
          ) : null}

          {lastPt ? (
            <circle cx={xAt(lastPt.i)} cy={yAt(lastPt.v)} r={4} fill="rgb(var(--c-live))" stroke="var(--card-bg)" strokeWidth={2} />
          ) : null}
        </svg>

        {hover ? (
          <div
            className="pointer-events-none absolute z-10 rounded border px-2 py-1 text-[11px] leading-4 shadow-sm"
            style={{
              left: `${hoverLeftPct}%`,
              top: `${hoverTopPct}%`,
              transform: `translate(${hoverLeftPct > 78 ? "-105%" : "8px"}, -110%)`,
              background: "var(--card-bg)",
              borderColor: "var(--card-border)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            <div style={{ opacity: 0.7 }}>{hover.label}</div>
            <div>
              {fmtUSD(hover.usd)}
              <span style={{ opacity: 0.7 }}> level</span> · {fmtPctSigned(hover.pct)}
              <span style={{ opacity: 0.7 }}>{mode === "return" ? " per unit" : ""}</span>
            </div>
          </div>
        ) : null}
      </div>

      <div
        className="mt-2 flex flex-wrap items-baseline gap-x-5 gap-y-1 text-[11px]"
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        <span>
          <span style={{ opacity: 0.65 }}>Last </span>
          <strong>{fmtUSD(lastUsd)}</strong>
          <span style={{ opacity: 0.65 }}> level</span>
        </span>
        <span
          title={
            mode === "return"
              ? "Per-unit return over the window: capital flows are divided out at their effective date, the same construction the administrator uses for NAV per share. A subscription raises the fund's level but not this line."
              : "NAV LEVEL over the window — this INCLUDES capital that came in or out, so a subscription reads as a step up. Switch to Return for performance."
          }
        >
          <span style={{ opacity: 0.65 }}>
            Δ {RANGE_LABEL[series.range] ?? series.range} {mode === "return" ? "per unit " : "level "}
          </span>
          <strong>{fmtPctSigned(mode === "return" ? unitPct : changePct)}</strong>
          {mode === "level" ? (
            <span style={{ opacity: 0.8 }}>
              {" "}
              ({changeUsd >= 0 ? "+" : "−"}
              {fmtUSD(Math.abs(changeUsd))})
            </span>
          ) : null}
        </span>
        {indexUsable ? (
          <span
            style={{ opacity: 0.65 }}
            title="The figure you are NOT looking at. The two differ by exactly the capital that entered or left the fund in this window — level includes it, per-unit does not."
          >
            {mode === "return"
              ? `level ${fmtPctSigned(changePct)}${series.flow_adjusted ? " (incl. flows)" : ""}`
              : `per unit ${fmtPctSigned(unitPct)}`}
          </span>
        ) : null}
        <span style={{ opacity: 0.65 }}>
          {baseIsPrior
            ? `vs close ${fmtUSD(base)}${series.base_date ? ` (${series.base_date.slice(5)})` : ""}`
            : "vs first mark in range"}
          {series.today_live ? " · today live" : ""}
        </span>
        <span
          style={{ opacity: 0.65 }}
          title={
            "Estimated and unofficial — NAV Fund Services is the sole official source of fund performance. " +
            "BEFORE the incentive: the official Net ROR is net of the administrator's accrued incentive " +
            "reallocation, so this reads above it in profitable periods. On the management fee the two " +
            "halves of this line differ and neither is hidden: closed months pinned to official NAV per " +
            "share are already NET of the management fee and fund expenses, while the current month after " +
            "the last strike is the raw broker book and is GROSS of the fee still accruing."
          }
        >
          estimated · before incentive
        </span>
        {indexUsable ? (
          <span className="inline-flex items-center gap-1">
            {(["return", "level"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setPlotMode(m)}
                className="rounded px-1.5 py-0.5"
                style={{
                  border: "1px solid var(--card-border)",
                  color: mode === m ? "var(--text-primary)" : "var(--text-secondary)",
                  background: mode === m ? "var(--surface-group)" : "transparent",
                  fontWeight: mode === m ? 600 : 400,
                }}
                aria-pressed={mode === m}
                title={
                  m === "return"
                    ? "Performance: capital flows divided out (NAVPS construction)"
                    : "Fund level in dollars: includes subscriptions and redemptions"
                }
              >
                {m === "return" ? "Return" : "NAV $"}
              </button>
            ))}
          </span>
        ) : null}
        {exPct !== null && !indexUsable ? (
          <span title={EX_FLOWS_HINT}>
            <span style={{ opacity: 0.65 }}>ex-flows </span>
            <strong>≈ {fmtPctSigned(exPct)}</strong>
          </span>
        ) : null}
        {series.flows?.length ? (
          <span style={{ opacity: 0.8 }}>
            {mode === "return"
              ? "Divided out of this line (marked ▲):"
              : "NAV level — flows & fee wires marked, not performance:"}{" "}
            {series.flows
              .map(
                (f) =>
                  `${f.amount >= 0 ? "+" : "−"}$${(Math.abs(f.amount) / 1000).toFixed(0)}K ${flowKindLabel(f.kind)} ${f.date.slice(5)}`,
              )
              .join(" · ")}
          </span>
        ) : null}
      </div>

      {mode === "return" && series.index_anchored ? (
        <div className="mt-0.5 text-[11px]" style={{ opacity: 0.65 }}>
          Closed months are pinned to the administrator's official NAV per share
          {series.index_anchored_from ? ` from ${series.index_anchored_from}` : ""} through{" "}
          {series.index_unanchored_from ?? "the last strike"}; the daily shape between those
          month-ends is from our own marks, and everything after the last strike is broker-derived
          and unaudited. Pinning stops our mark-basis history from accumulating into the line.
          {series.index_anchored_from && series.base_date && series.index_anchored_from > series.base_date
            ? ` The stretch from ${series.base_date} to ${series.index_anchored_from} predates the administrator's first strike and is our own marks alone — unaudited.`
            : ""}
        </div>
      ) : null}
      {mode === "return" && !series.index_anchored && series.index_anchor_possible && !minuteMode ? (
        <div className="mt-0.5 text-[11px]" style={{ color: "rgb(180,118,8)" }}>
          NOT anchored to the administrator's official NAV per share for this window — the line is
          chained from our own daily marks, whose basis changed more than once before 31 Aug 2026,
          so it can drift materially (measured: the YTD chain read 7pp above official per share
          before anchoring). Read it as indicative, not as a return.
        </div>
      ) : null}
      {mode === "return" && (series.settling_dates ?? []).length ? (
        <div className="mt-0.5 text-[11px]" style={{ opacity: 0.65 }}>
          {(series.settling_dates ?? []).length} session
          {(series.settling_dates ?? []).length === 1 ? "" : "s"} withheld while a subscription
          settled ({(series.settling_dates ?? []).slice(0, 4).join(", ")}
          {(series.settling_dates ?? []).length > 4 ? ", …" : ""}). Admitted capital is in the fund
          from the 1st but sits at the bank until the sweep reaches IBKR, so for those sessions the
          broker book holds only part of the fund and no honest per-unit return exists — the line
          breaks rather than drawing one.
        </div>
      ) : null}
      {mode === "return" && !(series.settling_dates ?? []).length
        && (series.flows ?? []).some((f) => f.amount > 0) ? (
        <div className="mt-0.5 text-[11px]" style={{ color: "rgb(180,118,8)" }}>
          A subscription is divided out from its effective date, but the Axos→IBKR sweep can land a
          day or two later — those sessions can read low and the sweep day can read high. Treat the
          days around ▲ as settling, not as performance.
        </div>
      ) : null}

      {anchorMarks.length ? (
        <div className="mt-0.5 text-[11px]" style={{ opacity: 0.65 }}>
          ○ official NAV per share (NAV Fund Services, month-end).{" "}
          {series.index_anchored
            ? "The line is PINNED to these, so it passes through each one — they mark which points are the administrator's audited figures and which stretches are our own interpolation between them. They do not sit below the line: the fee-and-carry gap you see in the tile is against the official Net ROR, which is additionally net of the accrued incentive; NAV per share is not."
            : "This window is NOT pinned to them, so any distance between a dot and the line is our estimate drifting from the audited per-share figure."}
        </div>
      ) : null}

      {emptySessions.length ? (
        <div className="mt-0.5 text-[11px]" style={{ color: "rgb(180,118,8)" }}>
          No minute capture for {emptySessions.join(", ")} — capture began 09-01-2026 and this view fills in
          from there.
        </div>
      ) : null}
      {!minuteMode && missingDays > 0 ? (
        <div className="mt-0.5 text-[11px]" style={{ color: "rgb(180,118,8)" }}>
          {missingDays} session{missingDays === 1 ? "" : "s"} in this range {missingDays === 1 ? "has" : "have"} no
          daily mark of record — the line breaks there.
        </div>
      ) : null}
      {series.pre_record_history ? (
        <div className="mt-0.5 text-[11px]" style={{ opacity: 0.65 }}>
          History before 08-31-2026 predates the daily mark-of-record discipline (rows were written on page-load
          cadence; marks were bid-basis before 08-27) — treat older shape as indicative.
        </div>
      ) : null}
    </div>
  );
};

export default NavIntradayChart;
